import { baseApi } from "./baseApi";

// Types
export interface SalesOverview {
  totalRevenue: number;
  totalOrders: number;
  averageOrderValue: number;
  completedOrders: number;
  cancelledOrders: number;
  pendingOrders: number;
  previousPeriod: {
    totalRevenue: number;
    totalOrders: number;
    revenueChange: number;
    ordersChange: number;
  };
}

export interface RevenueDataPoint {
  date: string;
  revenue: number;
  orders: number;
}

export interface PopularItem {
  id: string;
  name: string;
  image: string;
  category: string;
  quantitySold: number;
  revenue: number;
  orderCount: number;
}

export interface CategorySales {
  id: string;
  name: string;
  revenue: number;
  orderCount: number;
  itemsSold: number;
  percentage: number;
}

export interface OrderStatusDistribution {
  status: string;
  count: number;
  percentage: number;
}

export interface PaymentMethodDistribution {
  method: string;
  count: number;
  revenue: number;
  percentage: number;
}

export interface AnalyticsExportData {
  overview: SalesOverview;
  popularItems: PopularItem[];
  categorySales: CategorySales[];
  orderStatus: OrderStatusDistribution[];
  paymentMethods: PaymentMethodDistribution[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

export type GroupBy = "day" | "week" | "month";

export interface DateRangeParams {
  startDate: string;
  endDate: string;
}

export interface RevenueParams extends DateRangeParams {
  groupBy?: GroupBy;
}

export interface PopularItemsParams extends DateRangeParams {
  limit?: number;
}

/**
 * Analytics API slice
 * Handles all analytics-related API calls
 */
export const analyticsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get sales overview
    getSalesOverview: builder.query<
      { success: boolean; data: SalesOverview; message?: string },
      DateRangeParams
    >({
      query: ({ startDate, endDate }) => ({
        url: `/analytics/overview?startDate=${startDate}&endDate=${endDate}`,
        credentials: "include",
      }),
      providesTags: ["Analytics"],
    }),

    // Get revenue data for charts
    getRevenueData: builder.query<
      { success: boolean; data: RevenueDataPoint[]; message?: string },
      RevenueParams
    >({
      query: ({ startDate, endDate, groupBy = "day" }) => ({
        url: `/analytics/revenue?startDate=${startDate}&endDate=${endDate}&groupBy=${groupBy}`,
        credentials: "include",
      }),
      providesTags: ["Analytics"],
    }),

    // Get popular items
    getPopularItems: builder.query<
      { success: boolean; data: PopularItem[]; message?: string },
      PopularItemsParams
    >({
      query: ({ startDate, endDate, limit = 10 }) => ({
        url: `/analytics/popular-items?startDate=${startDate}&endDate=${endDate}&limit=${limit}`,
        credentials: "include",
      }),
      providesTags: ["Analytics"],
    }),

    // Get category sales
    getCategorySales: builder.query<
      { success: boolean; data: CategorySales[]; message?: string },
      DateRangeParams
    >({
      query: ({ startDate, endDate }) => ({
        url: `/analytics/categories?startDate=${startDate}&endDate=${endDate}`,
        credentials: "include",
      }),
      providesTags: ["Analytics"],
    }),

    // Get order status distribution
    getOrderStatusDistribution: builder.query<
      { success: boolean; data: OrderStatusDistribution[]; message?: string },
      DateRangeParams
    >({
      query: ({ startDate, endDate }) => ({
        url: `/analytics/orders-status?startDate=${startDate}&endDate=${endDate}`,
        credentials: "include",
      }),
      providesTags: ["Analytics"],
    }),

    // Get payment method distribution
    getPaymentMethodDistribution: builder.query<
      { success: boolean; data: PaymentMethodDistribution[]; message?: string },
      DateRangeParams
    >({
      query: ({ startDate, endDate }) => ({
        url: `/analytics/payment-methods?startDate=${startDate}&endDate=${endDate}`,
        credentials: "include",
      }),
      providesTags: ["Analytics"],
    }),

    // Get export data
    getExportData: builder.query<
      { success: boolean; data: AnalyticsExportData; message?: string },
      DateRangeParams
    >({
      query: ({ startDate, endDate }) => ({
        url: `/analytics/export?startDate=${startDate}&endDate=${endDate}`,
        credentials: "include",
      }),
      providesTags: ["Analytics"],
    }),
  }),
});

export const {
  useGetSalesOverviewQuery,
  useGetRevenueDataQuery,
  useGetPopularItemsQuery,
  useGetCategorySalesQuery,
  useGetOrderStatusDistributionQuery,
  useGetPaymentMethodDistributionQuery,
  useGetExportDataQuery,
  useLazyGetExportDataQuery,
} = analyticsApi;
